import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  TextField,
  Stack,
  Chip,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  IconButton,
  CircularProgress,
  ListItem,
  ListItemText,
  List,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { InvestmentThesis as InvestmentThesisType, GlobalThesisPayload } from '../../types';
import {
  updateGlobalThesis,
  createGlobalThesis,
  getGlobalThesisVersions,
  rollbackToVersion,
} from '../../services/api';
import { RulesEngineTab } from './RulesEngineTab';
import VersionDetailsDialog from './VersionDetailsDialog';
import ComparisonResultDialog from './ComparisonResultDialog';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`global-thesis-tabpanel-${index}`}
      aria-labelledby={`global-thesis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `global-thesis-tab-${index}`,
    'aria-controls': `global-thesis-tabpanel-${index}`,
  };
}

interface GlobalThesisViewProps {
  globalThesis: InvestmentThesisType | null;
  setCurrentView: (view: 'selection' | 'global-thesis' | 'fund-thesis' | 'firm-profile') => void;
  onBack: () => void;
  commonSectors?: string[];
  commonRegions?: string[];
  isPrivilegedUser: boolean;
  onRollback: () => void;
}

export const GlobalThesisView: React.FC<GlobalThesisViewProps> = ({
  globalThesis,
  setCurrentView,
  commonSectors = [],
  commonRegions = [],
  isPrivilegedUser,
  onRollback,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);

  // Thesis Settings State
  const [thesisName, setThesisName] = useState(globalThesis?.name || '');
  const [thesisDescription, setThesisDescription] = useState(globalThesis?.description || '');
  const [coreInvestmentThemes, setCoreInvestmentThemes] = useState<string[]>(globalThesis?.core_investment_themes || []);
  const [valueCreationLevers, setValueCreationLevers] = useState<string[]>(globalThesis?.value_creation_levers || []);
  const [dealBreakers, setDealBreakers] = useState<string[]>(globalThesis?.deal_breakers || []);
  const [preferredBusinessModels, setPreferredBusinessModels] = useState<string[]>(globalThesis?.preferred_business_models || []);
  const [sectorFocus, setSectorFocus] = useState<string[]>(globalThesis?.firm_profile?.sector_focus || []);
  const [geographicFocus, setGeographicFocus] = useState<string[]>(globalThesis?.firm_profile?.geographic_focus || []);
  const [industryFocus, setIndustryFocus] = useState<string[]>(globalThesis?.firm_profile?.industry_focus || []);
  const [macroTrends, setMacroTrends] = useState<string[]>(globalThesis?.macro_trends || []);
  const [microTrends, setMicroTrends] = useState<string[]>(globalThesis?.micro_trends || []);
  const [riskAppetite, setRiskAppetite] = useState<'Low' | 'Medium' | 'High'>(
    globalThesis?.risk_appetite ? (globalThesis.risk_appetite.charAt(0).toUpperCase() + globalThesis.risk_appetite.slice(1).toLowerCase()) as 'Low' | 'Medium' | 'High' : 'Medium'
  );

  // Dialog State
  const [addItemDialog, setAddItemDialog] = useState(false);
  const [addItemType, setAddItemType] = useState<'theme' | 'dealBreaker' | 'macroTrend' | 'microTrend'>('theme');
  const [newItemValue, setNewItemValue] = useState('');

  // Rules Engine State
  const [versions, setVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any | null>(null);
  const [isVersionDetailsOpen, setIsVersionDetailsOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<{ version1: any; version2: any } | null>(null);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);


  useEffect(() => {
    if (globalThesis) {
      setThesisName(globalThesis.name || '');
      setThesisDescription(globalThesis.description || '');
      setCoreInvestmentThemes(globalThesis.core_investment_themes || []);
      setValueCreationLevers(globalThesis.value_creation_levers || []);
      setDealBreakers(globalThesis.deal_breakers || []);
      setPreferredBusinessModels(globalThesis.preferred_business_models || []);
      setSectorFocus(globalThesis.firm_profile?.sector_focus || []);
      setGeographicFocus(globalThesis.firm_profile?.geographic_focus || []);
      setIndustryFocus(globalThesis.firm_profile?.industry_focus || []);
      setMacroTrends(globalThesis.macro_trends || []);
      setMicroTrends(globalThesis.micro_trends || []);
      setRiskAppetite(
        globalThesis.risk_appetite
          ? (globalThesis.risk_appetite.charAt(0).toUpperCase() +
            globalThesis.risk_appetite.slice(1).toLowerCase() as 'Low' | 'Medium' | 'High')
          : 'Medium'
      );
    }
  }, [globalThesis]);

  useEffect(() => {
    if (tabValue === 2 && globalThesis?.id) {
      const fetchVersions = async () => {
        setLoadingVersions(true);
        try {
          const data = await getGlobalThesisVersions();
          setVersions(data);
        } catch (error) {
          console.error('Error fetching versions:', error);
        } finally {
          setLoadingVersions(false);
        }
      };
      fetchVersions();
    }
  }, [tabValue, globalThesis?.id]);

  const handleViewVersionDetails = (version: any) => {
    setSelectedVersion(version);
    setIsVersionDetailsOpen(true);
  };

  const handleCompareVersions = () => {
    if (selectedVersions.length !== 2) {
      window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'Please select exactly two versions to compare.' }));
      return;
    }

    const version1 = versions.find(v => v.id === selectedVersions[0]);
    const version2 = versions.find(v => v.id === selectedVersions[1]);

    if (!version1 || !version2) {
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Could not find selected versions.' }));
      return;
    }

    setComparisonData({ version1, version2 });
    setIsComparisonOpen(true);
  };

  const handleVersionSelection = (versionId: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      }
      if (prev.length < 2) {
        return [...prev, versionId];
      }
      window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'You can only select up to two versions to compare.' }));
      return prev;
    });
  };

  const handleRollback = (versionId: string) => {
    setSelectedVersionId(versionId);
    setRollbackDialogOpen(true);
  };

  const confirmRollback = async () => {
    if (!globalThesis?.id || !selectedVersionId) return;
    setIsRollingBack(true);
    try {
      await rollbackToVersion(globalThesis.id, selectedVersionId);
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Rollback successful. A new version has been created.' }));
      const data = await getGlobalThesisVersions();
      setVersions(data);
      onRollback();
    } catch (error) {
      console.error('Error rolling back:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to roll back.' }));
    } finally {
      setRollbackDialogOpen(false);
      setSelectedVersionId(null);
      setIsRollingBack(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddItem = (type: 'theme' | 'dealBreaker' | 'macroTrend' | 'microTrend') => {
    setAddItemType(type);
    setAddItemDialog(true);
  };

  const confirmAddItem = () => {
    if (!newItemValue.trim()) return;
    switch (addItemType) {
      case 'theme':
        setCoreInvestmentThemes([...coreInvestmentThemes, newItemValue.trim()]);
        break;
      case 'dealBreaker':
        setDealBreakers([...dealBreakers, newItemValue.trim()]);
        break;
      case 'macroTrend':
        setMacroTrends([...macroTrends, newItemValue.trim()]);
        break;
      case 'microTrend':
        setMicroTrends([...microTrends, newItemValue.trim()]);
        break;
    }
    setNewItemValue('');
    setAddItemDialog(false);
  };

  const saveGlobalThesis = async () => {
    setSaving(true);
    try {
      const  thesisPayload: GlobalThesisPayload = {
        name: thesisName,
        description: thesisDescription,
        core_investment_themes: coreInvestmentThemes,
        value_creation_levers: valueCreationLevers,
        deal_breakers: dealBreakers,
        preferred_business_models: preferredBusinessModels,
        macro_trends: macroTrends,
        micro_trends: microTrends,
        risk_appetite: riskAppetite.toLowerCase() as 'low' | 'medium' | 'high',
        is_global: true,
        fund_id: null,
        // Include firm profile related fields directly in the global thesis payload
        sector_focus: sectorFocus,
        geographic_focus: geographicFocus,
        industry_focus: industryFocus,
      };

      if (globalThesis?.id) {
        // For updates, we still need to update the firm profile separately if it exists
        // However, the user's instruction implies these fields should be part of the global thesis payload directly.
        // The updateFirmProfile call will be removed as per user's instruction.
        await updateGlobalThesis(globalThesis.id, thesisPayload);
        window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Global thesis saved successfully!' }));
      } else {
        await createGlobalThesis(thesisPayload);
        window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Global thesis created successfully!' }));
        setCurrentView('selection');
      }
    } catch (err: any) {
      console.error('Error saving global thesis:', err);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to save changes: ' + (err.response?.data?.detail || err.message) }));
    } finally {
      setSaving(false);
    }
  };

  // Remove the useEffect that was fetching sectors and regions
  // This data is now passed as props from the parent component

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Global Investment Thesis</Typography>
            {!isPrivilegedUser && (
              <Tooltip title="Read-only: Only Admin users can edit the global investment thesis">
                <LockIcon color="action" fontSize="small" />
              </Tooltip>
            )}
          </Box>
          <Button
            startIcon={<BackIcon />}
            onClick={() => setCurrentView('selection')}
            variant="outlined"
            size="small"
          >
            Back to Main Menu
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="global thesis tabs">
            <Tab label="Thesis Settings" {...a11yProps(0)} />
            <Tab label="Rules Engine" {...a11yProps(1)} disabled={!globalThesis?.id} />
            <Tab label="Version History" {...a11yProps(2)} disabled={!globalThesis?.id} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h5" gutterBottom>🌍 Global Investment Thesis</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Define your organization-wide investment strategy that applies to all funds and deals.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 3 }}>
            <TextField
              fullWidth
              label="Thesis Name"
              value={thesisName}
              onChange={(e) => setThesisName(e.target.value)}
              placeholder="e.g., Global Investment Thesis 2024"
              disabled={!isPrivilegedUser}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={thesisDescription}
              onChange={(e) => setThesisDescription(e.target.value)}
              placeholder="Describe your overall investment philosophy and approach..."
              disabled={!isPrivilegedUser}
            />

            {/* Core Investment Themes */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Core Investment Themes</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define the key themes that drive your investment decisions.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {coreInvestmentThemes.map((theme, index) => (
                  <Chip
                    key={index}
                    label={theme}
                    onDelete={isPrivilegedUser ? () => setCoreInvestmentThemes(coreInvestmentThemes.filter((_, i) => i !== index)) : undefined}
                    sx={{ m: 0.5 }}
                  />
                ))}
                {isPrivilegedUser && (
                  <Chip
                    icon={<AddIcon />}
                    label="Add Theme"
                    variant="outlined"
                    onClick={() => handleAddItem('theme')}
                    sx={{ m: 0.5 }}
                  />
                )}
              </Stack>
            </Box>

            {/* Value Creation Levers */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Value Creation Levers</Typography>
              <Autocomplete
                multiple
                options={['Operational Efficiency', 'Market Expansion', 'Product Development', 'M&A Integration', 'Talent Acquisition', 'Technology Upgrade', 'Cost Optimization', 'Revenue Growth', 'Digital Transformation', 'ESG Implementation']}
                value={valueCreationLevers}
                onChange={(_, newValue) => setValueCreationLevers(newValue)}
                disabled={!isPrivilegedUser}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select value creation levers" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
              />
            </Box>

            {/* Deal Breakers */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Deal Breakers</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define absolute criteria that would disqualify an investment opportunity.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {dealBreakers.map((breaker, index) => (
                  <Chip
                    key={index}
                    label={breaker}
                    onDelete={isPrivilegedUser ? () => setDealBreakers(dealBreakers.filter((_, i) => i !== index)) : undefined}
                    color="error"
                    variant="outlined"
                    sx={{ m: 0.5 }}
                  />
                ))}
                {isPrivilegedUser && (
                  <Chip
                    icon={<AddIcon />}
                    label="Add Deal Breaker"
                    variant="outlined"
                    onClick={() => handleAddItem('dealBreaker')}
                    sx={{ m: 0.5 }}
                  />
                )}
              </Stack>
            </Box>

            {/* Preferred Business Models */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Preferred Business Models</Typography>
              <Autocomplete
                multiple
                options={['SaaS', 'Marketplace', 'Subscription', 'E-commerce', 'B2B Services', 'Manufacturing', 'Asset-Light', 'Platform', 'Franchise', 'Licensing', 'Freemium', 'Direct-to-Consumer']}
                value={preferredBusinessModels}
                onChange={(_, newValue) => setPreferredBusinessModels(newValue)}
                disabled={!isPrivilegedUser}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select preferred business models" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
              />
            </Box>

            {/* Sector Focus */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Sector Focus</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define specific sectors this fund thesis focuses on.
              </Typography>
              <Autocomplete
                multiple
                options={commonSectors}
                value={sectorFocus}
                onChange={(_, newValue) => setSectorFocus(newValue)}
                disabled={!isPrivilegedUser}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select sectors" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
              />
            </Box>

            {/* Geographic Focus */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Geographic Focus</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define specific geographic regions for investment.
              </Typography>
              <Autocomplete
                multiple
                options={commonRegions}
                value={geographicFocus}
                onChange={(_, newValue) => setGeographicFocus(newValue)}
                disabled={!isPrivilegedUser}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select regions" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
              />
            </Box>

            {/* Industry Focus */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Industry Focus</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define specific industries within the selected sectors.
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                options={[
                  'Software & Technology', 'Healthcare Services', 'Financial Technology',
                  'Real Estate Technology', 'Clean Energy', 'Consumer Products',
                  'Industrial Automation', 'Telecommunications Infrastructure',
                  'Digital Media', 'Transportation & Logistics', 'EdTech',
                  'AgTech', 'E-commerce Platforms', 'Advanced Manufacturing',
                  'Biotechnology & Life Sciences'
                ]}
                value={industryFocus}
                onChange={(_, newValue) => setIndustryFocus(newValue)}
                disabled={!isPrivilegedUser}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select or type industries" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
              />
            </Box>

            {/* Macro Trends */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Macro Trends</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Identify macro-economic and societal trends that influence your investment decisions.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {macroTrends.map((trend, index) => (
                  <Chip
                    key={index}
                    label={trend}
                    onDelete={isPrivilegedUser ? () => setMacroTrends(macroTrends.filter((_, i) => i !== index)) : undefined}
                    color="primary"
                    variant="outlined"
                    sx={{ m: 0.5 }}
                  />
                ))}
                {isPrivilegedUser && (
                  <Chip
                    icon={<AddIcon />}
                    label="Add Macro Trend"
                    variant="outlined"
                    onClick={() => handleAddItem('macroTrend')}
                    sx={{ m: 0.5 }}
                  />
                )}
              </Stack>
            </Box>

            {/* Micro Trends */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Micro Trends</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Identify sector-specific or niche trends that create investment opportunities.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {microTrends.map((trend, index) => (
                  <Chip
                    key={index}
                    label={trend}
                    onDelete={isPrivilegedUser ? () => setMicroTrends(microTrends.filter((_, i) => i !== index)) : undefined}
                    color="secondary"
                    variant="outlined"
                    sx={{ m: 0.5 }}
                  />
                ))}
                {isPrivilegedUser && (
                  <Chip
                    icon={<AddIcon />}
                    label="Add Micro Trend"
                    variant="outlined"
                    onClick={() => handleAddItem('microTrend')}
                    sx={{ m: 0.5 }}
                  />
                )}
              </Stack>
            </Box>

            {/* Risk Appetite */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Risk Appetite</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define your organization's overall risk tolerance for investments.
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={riskAppetite}
                  onChange={(e) => setRiskAppetite(e.target.value as 'Low' | 'Medium' | 'High')}
                >
                  <FormControlLabel
                    value="Low"
                    control={<Radio />}
                    disabled={!isPrivilegedUser}
                    label={
                      <Box>
                        <Typography variant="body2">Low - Conservative</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Established companies, proven business models
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="Medium"
                    control={<Radio />}
                    disabled={!isPrivilegedUser}
                    label={
                      <Box>
                        <Typography variant="body2">Medium - Balanced</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Mix of growth and stability
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="High"
                    control={<Radio />}
                    disabled={!isPrivilegedUser}
                    label={
                      <Box>
                        <Typography variant="body2">High - Aggressive</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Early-stage, high-growth potential
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={() => setCurrentView('selection')}
            >
              Cancel
            </Button>
            {isPrivilegedUser && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveGlobalThesis}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Global Thesis'}
              </Button>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <RulesEngineTab thesisId={globalThesis?.id || null} isPrivilegedUser={isPrivilegedUser} disableAddRule={!isPrivilegedUser} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h5" gutterBottom>📋 Version History</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Track changes to your global thesis over time.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>Thesis Versions</Typography>
            <Button
              variant="contained"
              onClick={handleCompareVersions}
              disabled={selectedVersions.length !== 2}
            >
              Compare Selected
            </Button>
          </Box>
          {loadingVersions ? (
            <CircularProgress />
          ) : (
            <Paper variant="outlined">
              <List>
                {versions.map((version, index) => (
                  <React.Fragment key={version.id}>
                    <ListItem>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Switch
                          checked={selectedVersions.includes(version.id)}
                          onChange={() => handleVersionSelection(version.id)}
                          size="small"
                        />
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">Version {version.version_number}</Typography>
                              {index === 0 && <Chip label="Current" color="primary" size="small" />}
                            </Box>
                          }
                          secondary={`Updated on ${new Date(version.created_at).toLocaleDateString()} by ${version.creator?.email || 'N/A'}`}
                        />
                      </Stack>
                      <Box sx={{ flexGrow: 1 }} />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton edge="end" aria-label="view" onClick={() => handleViewVersionDetails(version)}>
                          <VisibilityIcon />
                        </IconButton>
                        {isPrivilegedUser && (
                          <Tooltip title="Rollback to this version">
                            <span>
                              <IconButton
                                edge="end"
                                aria-label="rollback"
                                onClick={() => handleRollback(version.id)}
                                disabled={index === 0}
                              >
                                <HistoryIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </ListItem>
                    {index < versions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </TabPanel>
      </Paper>

      {selectedVersion && (
        <VersionDetailsDialog
          open={isVersionDetailsOpen}
          onClose={() => setIsVersionDetailsOpen(false)}
          version={selectedVersion}
          viewType="global"
        />
      )}

      <ComparisonResultDialog
        open={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        version1={comparisonData?.version1}
        version2={comparisonData?.version2}
      />

      {/* Add Item Dialog */}
      <Dialog open={addItemDialog} onClose={() => setAddItemDialog(false)}>
        <DialogTitle>
          Add {addItemType === 'theme' ? 'Investment Theme' :
               addItemType === 'dealBreaker' ? 'Deal Breaker' :
               addItemType === 'macroTrend' ? 'Macro Trend' :
               addItemType === 'microTrend' ? 'Micro Trend' : 'Item'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={addItemType === 'theme' ? 'Theme Name' : 
                   addItemType === 'dealBreaker' ? 'Deal Breaker' :
                   addItemType === 'macroTrend' ? 'Macro Trend' :
                   addItemType === 'microTrend' ? 'Micro Trend' : 'Item'}
            fullWidth
            variant="outlined"
            value={newItemValue}
            onChange={(e) => setNewItemValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newItemValue.trim()) {
                confirmAddItem();
              }
            }}
            placeholder={
              addItemType === 'theme' ? 'e.g., Digital Transformation, Sustainability' :
              addItemType === 'dealBreaker' ? 'e.g., Regulatory issues, Poor management' :
              addItemType === 'macroTrend' ? 'e.g., Remote work adoption, ESG focus' :
              addItemType === 'microTrend' ? 'e.g., AI in healthcare, Fintech disruption' : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmAddItem}
            variant="contained"
            disabled={!newItemValue.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rollbackDialogOpen} onClose={() => setRollbackDialogOpen(false)}>
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to roll back to this version? This action will create a new version with the content of the selected version.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialogOpen(false)} disabled={isRollingBack}>Cancel</Button>
          <Button onClick={confirmRollback} color="primary" disabled={isRollingBack}>
            {isRollingBack ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
